import { createAuth0Client } from '@auth0/auth0-spa-js';
import Auth0Helper from '@shipstation/auth0-helpers';

import { Auth0TokenRefreshHelper } from './auth0-token-refresh-helper';
import * as AuthConstants from '../../authentication/constants';
import AuthMessages from '../../authentication/login/messages';
import * as Cookies from '../../constants/cookies';
import { getLaunchDarklyAnonymousFeatureFlags } from '../../feature-flags/config';
import { CookieService } from '../../services/cookie-service';
import { LogService } from '../../services/log-service';

const { Auth0LinkingHelper, Auth0BetaOptInHelper, Auth0UserSession } = Auth0Helper;

class Auth0WrapperClass {
  CLAIMS = {
    IDS_SESSION_ID: 'http://shipstation.com/ids_session_id',
    IS_ACCOUNT_OWNER: 'http://shipstation.com/is_account_owner',
  };

  constructor() {
    this.aut0ClientId = window.SS_GLOBALS.frontEndConfig.auth0ClientId;

    this.options = {
      clientId: this.aut0ClientId,
      domain: window.SS_GLOBALS.frontEndConfig.auth0Domain,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: !this.isRestoreAuth0LocalStorageEnabled,
      authorizationParams: {
        audience: window.SS_GLOBALS.frontEndConfig.auth0ApiAudience,
        connection: window.SS_GLOBALS.frontEndConfig.auth0Connection,
        scope: 'openid profile email offline_access',
      },
    };

    // before any part of Auth0 is initialized, check if we need to restore localStorage from a different partition
    this.enableAuth0HelperSentry();
    this.maybeRestoreAuth0LocalStorage();

    this.accessToken = null;
    this.tokenPromise = null;

    this.initPromise = createAuth0Client(this.options)
      .then(async (client) => {
        this.auth0 = client;
        this.tokenRefreshHelper = new Auth0TokenRefreshHelper(client);
        const allFlags = await this.getAnonymousFeatureFlags();
        const { restoreOldRefreshTokenBehavior } = allFlags;
        this.restoreOldRefreshTokenBehavior = restoreOldRefreshTokenBehavior;
      })
      .catch((error) => {
        this.emitSentryAuthError('a0Wrapper.init.error', error);
      });
  }

  get managementApi() {
    if (this.managementApiRef) return this.managementApiRef;

    this.managementApiRef = new Auth0Helper.Auth0ManagementApi({
      tenantOrDomain: this.options.domain,
      clientId: window.SS_GLOBALS.frontEndConfig.auth0ClientId,
      auth0SpaJsCreateClient: createAuth0Client,
    });

    return this.managementApiRef;
  }

  get supportedIdentityProvidersApi() {
    if (this.supportedIdPsApiRef) return this.supportedIdPsApiRef;

    this.supportedIdPsApiRef = new Auth0Helper.Auth0SupportedIdps({
      tenantOrDomain: this.options.domain,
      activeIdps: AuthConstants.ActiveIdentityProviders,
    });
    return this.supportedIdPsApiRef;
  }

  get supportedIdentityProviders() {
    return this.supportedIdentityProvidersApi.supportedIdps;
  }

  get activeIdentityProviders() {
    return this.supportedIdentityProvidersApi.activeIdps;
  }

  get accountLinkingRequestResult() {
    return Auth0LinkingHelper.accountLinkingRequestResult;
  }

  get isRestoreAuth0LocalStorageEnabled() {
    return window.SS_GLOBALS.frontEndConfig.auth0EnableRestoreLocalStorage;
  }

  getAnonymousFeatureFlags() {
    const anonFlagsEncoded = getLaunchDarklyAnonymousFeatureFlags();
    if (!anonFlagsEncoded?.length) return {};

    try {
      const decodedFlags = atob(anonFlagsEncoded);
      return JSON.parse(decodedFlags);
    } catch {
      return {};
    }
  }

  maybeRestoreAuth0LocalStorage() {
    if (!this.isRestoreAuth0LocalStorageEnabled) return;
    // If Auth was fielded on a different partition than the current partition, the localStorage entries made by the auth0-spajs
    // will be gone. The auth0-config package can compensate for this by storing them in cookies temporarily and restoring
    // them into localStorage where Auth0 expects them to be.
    Auth0Helper.restoreAuth0LocalStorage();
  }

  enableAuth0HelperSentry() {
    try {
      Auth0Helper.enableSentry({
        tenantOrDomain: this.options.domain,
        sentryDsn: window.SS_GLOBALS.frontEndConfig.auth0SentryDsn,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed initializing auth sentry. ${error.message}`);
    }
  }

  startLinkWithIdpFlow(idp) {
    Auth0Helper.Auth0LinkingHelper.startLinkWithIdpFlow(idp, `${window.location.origin}/loginauth`);
  }

  isUsingAuth0Auth() {
    return !CookieService.getCookieValue(Cookies.LegacyAuth);
  }

  isAuthenticated() {
    const auth0IsAuthenticated = () => (this.auth0 ? this.auth0.isAuthenticated() : Promise.resolve(false));

    if (this.initPromise) {
      return this.initPromise.then(() => {
        return auth0IsAuthenticated();
      });
    }
    return auth0IsAuthenticated();
  }

  getIdTokenClaims() {
    const getAuth0IdTokenClaims = () => {
      if (!this.auth0) {
        return Promise.resolve(null);
      }
      return this.auth0.getIdTokenClaims();
    };

    if (this.initPromise) {
      return this.initPromise.then(() => {
        return getAuth0IdTokenClaims();
      });
    }

    return getAuth0IdTokenClaims();
  }

  async getAccessToken(shouldSkipCache = false) {
    if (this.initPromise) await this.initPromise;

    if (this.restoreOldRefreshTokenBehavior) {
      return this.getAccessTokenOld(shouldSkipCache);
    }

    if (!this.auth0) {
      const e = new Error('Auth0NotInitialized');
      this.emitSentryAuthError('a0Wrapper.accessToken.clientInitError', e);
      throw e;
    }

    if (this.tokenPromise) return this.tokenPromise;
    if (this.accessToken && !shouldSkipCache && !this.tokenRefreshHelper.isJwtExpired(this.accessToken)) return this.accessToken;

    this.tokenPromise = this.tokenRefreshHelper
      .getAccessToken(shouldSkipCache, (newAt) => {
        this.accessToken = newAt;
      })
      .catch((error) => {
        this.emitSentryAuthError('a0Wrapper.accessToken.getTokenError', error);
        throw error;
      })
      .finally(() => {
        this.tokenPromise = null;
      });

    return this.tokenPromise;
  }

  getAccessTokenOld(shouldSkipCache = false) {
    const promoteAuth0CookieDomain = () => {
      const hostWithoutPort = window.location.host.split(':')[0];
      CookieService.removeCookie(Cookies.Auth0IsAuthenticated, hostWithoutPort);
      CookieService.setCookieValueWithExpiration(Cookies.Auth0IsAuthenticated, 'true', 2);
      CookieService.removeCookie(Cookies.Auth0IsClientAuthenticated, hostWithoutPort);
      CookieService.setCookieValueWithExpiration(Cookies.Auth0IsClientAuthenticated, 'true', 2);
    };

    const auth0GetAccessToken = () => {
      if (!this.auth0) return Promise.resolve(null);

      return this.auth0.getTokenSilently({ cacheMode: shouldSkipCache ? 'off' : 'on' }).then((accessToken) => {
        promoteAuth0CookieDomain();
        if (accessToken) {
          if (this.accessToken !== accessToken) {
            this.accessToken = accessToken;
            CookieService.setCookieValueWithPathAndDomain(Cookies.AccessTokenV3, accessToken);
            CookieService.setCookieValueWithPathAndDomain(Cookies.AccessTokenV2, accessToken);
          }
        }
        return accessToken;
      });
    };

    if (this.initPromise) {
      return this.initPromise.then(() => {
        return auth0GetAccessToken();
      });
    }
    return auth0GetAccessToken();
  }

  emitSentryAuthError(eventName, error, tags = {}, extra = {}) {
    try {
      const markerTags = {
        flow: 'a0',
      };

      const authState = {
        cookieSignedInVal: CookieService.getCookieValue(Cookies.Auth0IsAuthenticated) || 'not present',
        cookieClientSignedInVal: CookieService.getCookieValue(Cookies.Auth0IsClientAuthenticated) || 'not present',
        cookieSsHome: CookieService.getCookieValue(Cookies.SSHome) || 'not present',
        cookieLatCookiePresent: !!CookieService.getCookieValue(Cookies.AccessTokenV2),
        cookieLatV3CookiePresent: !!CookieService.getCookieValue(Cookies.AccessTokenV3),
        lsIdt: Auth0UserSession?.userUserName || 'not present',
        lsIssuingClientId: Auth0UserSession?.userAccessTokenClientId || 'not present',
        lsAtExpiresAt: Auth0UserSession?.userAccessTokenExpiry || 'not present',
        lsRtPresent: !!Auth0UserSession?.userRefreshToken,
        lsSessionId: Auth0UserSession?.userSessionId || 'not present',
        refreshErrorState: this.tokenRefreshHelper?.silentAuthErrorState
          ? JSON.stringify(this.tokenRefreshHelper?.silentAuthErrorState)
          : 'no errors',
        reportedError: error,
      };

      const payload = {
        tags: {
          ...tags,
          ...markerTags,
        },
        extra: {
          ...extra,
          ...authState,
        },
      };

      // In non-dev this will send to Sentry as an exception (with user scope)
      LogService.logError(`Auth0 error event [${eventName}]`, payload);
    } catch (reportError) {
      LogService.logError(`Auth0 error event attempting detailed auth Sentry report[${eventName}]`, reportError);
    }
  }

  logout(returnTo, logoutType) {
    const auth0Logout = () => {
      if (this.auth0) {
        if (logoutType === AuthConstants.LogoutTypeMaintenance) {
          CookieService.setCookieValueWithExpirationInMinutes(
            Cookies.AuthErrorOrLogoutMsg,
            AuthMessages.sellerMaintenanceLogoutMessage.defaultMessage,
            15,
          );
        }

        let returnToWithFallback = returnTo;
        if (!returnTo || returnTo.indexOf('/') === 0) {
          returnToWithFallback = returnTo ? document.location.origin + returnTo : document.location.origin;
        }
        return this.auth0.logout({
          logoutParams: { returnTo: returnToWithFallback },
        });
      }
    };

    if (this.initPromise) {
      return this.initPromise.then(() => {
        return auth0Logout();
      });
    }
    return new Promise((resolve) => {
      resolve(auth0Logout());
    });
  }

  isNativeSsoSeller() {
    return Auth0UserSession.isNativeSsoSeller;
  }

  get optInApi() {
    if (!this.optInHelperRef) {
      this.optInHelperRef = new Auth0BetaOptInHelper({
        tenantOrDomain: this.options.domain,
        ldClientSideId: window.SS_GLOBALS.frontEndConfig.launchDarklyClientSideId,
      });
    }
    return this.optInHelperRef;
  }
}

export const Auth0Wrapper = new Auth0WrapperClass();
